import { useEffect, useState } from "react";
import { useOnlineStatus } from "../hooks/useOnlineStatus";

/**
 * Slim banner that appears when the device goes offline. Stays visible
 * during the offline period plus 2s after reconnect with a brief "Online"
 * confirmation, then auto-hides.
 *
 * Renders nothing when steadily online — never distracts during normal
 * operation.
 */
export function OfflineBanner({
  variant = "mobile",
}: {
  variant?: "mobile" | "desktop";
}) {
  const online = useOnlineStatus();
  const [showReconnected, setShowReconnected] = useState(false);
  const [hadOffline, setHadOffline] = useState(false);

  useEffect(() => {
    if (!online) {
      setHadOffline(true);
      setShowReconnected(false);
      return;
    }
    if (hadOffline) {
      setShowReconnected(true);
      const t = setTimeout(() => setShowReconnected(false), 2400);
      return () => clearTimeout(t);
    }
  }, [online, hadOffline]);

  if (online && !showReconnected) return null;

  const positionClass =
    variant === "desktop"
      ? "fixed top-16 left-1/2 -translate-x-1/2 z-40"
      : "fixed top-[max(env(safe-area-inset-top),12px)] mt-12 left-2 right-2 z-40";

  return (
    <div
      role="status"
      aria-live="polite"
      className={`${positionClass} pointer-events-none`}
    >
      <div
        className={`flex items-center gap-2 px-3 py-2 rounded-full text-2xs font-medium shadow-soft transition-all duration-300 ease-spring ${
          online
            ? "bg-ok/15 text-ok border border-ok/30"
            : "bg-warn/15 text-warn border border-warn/40"
        }`}
        style={{ backdropFilter: "blur(12px)" }}
      >
        <span
          className={`w-1.5 h-1.5 rounded-full ${
            online ? "bg-ok" : "bg-warn animate-pulseSoft"
          }`}
        />
        {online ? (
          <span>Verbinding hersteld — wijzigingen synchroniseren</span>
        ) : (
          <span>Offline — wijzigingen syncen bij reconnect</span>
        )}
      </div>
    </div>
  );
}
